import React, { useCallback } from 'react'

import type { LoadMoreNotificationCardProps } from './LoadMoreNotificationCard.types'
import * as S from './LoadMoreNotificationCard.styles'

export function LoadMoreNotificationCard({
    remainingCount,
    onLoadMore,
    isLoading = false,
}: LoadMoreNotificationCardProps) {
    const handleClick = useCallback(() => {
        if (!isLoading) {
            onLoadMore()
        }
    }, [isLoading, onLoadMore])

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if ((event.key === 'Enter' || event.key === ' ') && !isLoading) {
                event.preventDefault()
                onLoadMore()
            }
        },
        [isLoading, onLoadMore],
    )

    return (
        <S.LoadMoreCardContainer
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={!isLoading ? { scale: 1.01 } : undefined}
            whileTap={!isLoading ? { scale: 0.99 } : undefined}
            transition={{ duration: 0.2 }}
            $isLoading={isLoading}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
            aria-label={`Load ${remainingCount} more notifications`}
            aria-disabled={isLoading}
        >
            {isLoading && (
                <S.LoadMoreIcon $isLoading={isLoading}>
                    <S.LoadMoreSpinner
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    />
                </S.LoadMoreIcon>
            )}

            <S.LoadMoreContent>
                <S.LoadMoreTitle>
                    {isLoading ? <>Loading more notifications...</> : `Show ${remainingCount} more`}
                </S.LoadMoreTitle>
            </S.LoadMoreContent>
        </S.LoadMoreCardContainer>
    )
}

// Export component with display name for debugging
LoadMoreNotificationCard.displayName = 'LoadMoreNotificationCard'
